import { z } from 'zod';
import { PageElementSchema } from './page-elements';

// ========== Base Node Schema ==========
const BaseNodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string().optional(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
});

// ========== Node Type Schemas ==========
// NOTE: Start and End nodes kept for backwards compatibility with existing playbooks
// but removed from palette. Pages should be used as entry/exit points instead.
const StartNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Start'),
  next: z.string().optional(),
});

const EndNodeSchema = BaseNodeSchema.extend({
  type: z.literal('End'),
  outcome: z.string().optional(),
});

const NoteNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Note'),
  content: z.string(),
  next: z.string().optional(),
});

const PromptNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Prompt'),
  content: z.string(),
  varId: z.string(), // GUID of the variable this prompt writes to
  next: z.string().optional(),
});

const FormFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'email', 'tel', 'number', 'textarea', 'select', 'checkbox', 'radio']),
  required: z.boolean().default(false),
  options: z.array(z.string()).optional(),
  isPII: z.boolean().optional(),
  varId: z.string().optional(), // GUID of the variable this field writes to
});

const FormNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Form'),
  title: z.string(),
  fields: z.array(FormFieldSchema),
  next: z.string().optional(),
});

const ConditionSchema = z.object({
  path: z.string(),
  operator: z.enum(['==', '!=', 'contains', 'truthy']),
  value: z.any().optional(),
});

const IfNodeSchema = BaseNodeSchema.extend({
  type: z.literal('If'),
  condition: ConditionSchema,
  then: z.string().optional(),
  else: z.string().optional(),
});

const BranchPathSchema = z.object({
  label: z.string(),
  target: z.string().optional(),
  description: z.string().optional(),
});

const BranchNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Branch'),
  title: z.string().optional(),
  description: z.string().optional(),
  branches: z.array(BranchPathSchema),
});

const SwitchCaseSchema = z.object({
  condition: ConditionSchema.optional(),
  next: z.string(),
});

const SwitchNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Switch'),
  cases: z.array(SwitchCaseSchema),
  defaultNext: z.string().optional(),
});

const SetVarNodeSchema = BaseNodeSchema.extend({
  type: z.literal('SetVar'),
  varId: z.string(), // GUID of the variable this node writes to
  valueTemplate: z.string(),
  next: z.string().optional(),
});

// ========== New UI Element Node Schemas ==========
const PageNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Page'),
  title: z.string(),
  content: z.string().optional(),
  layoutType: z.enum(['single-column', 'two-column', 'sidebar']).default('single-column'),
  // Contact Us Menu shortcuts
  contactMenu: z.object({
    enableCallNow: z.boolean().default(false),
    enableCallReturn: z.boolean().default(false),
    enableEmail: z.boolean().default(false),
    phoneNumber: z.string().optional(),
    email: z.string().optional(),
  }).optional(),
  // Page elements (nested UI builder)
  elements: z.array(PageElementSchema).default([]),
  next: z.string().optional(),
});

const ContactUsNodeSchema = BaseNodeSchema.extend({
  type: z.literal('ContactUs'),
  title: z.string(),
  description: z.string().optional(),
  fields: z.array(FormFieldSchema),
  submitButtonText: z.string().default('Submit'),
  next: z.string().optional(),
});

const MenuItemSchema = z.object({
  label: z.string(),
  target: z.string(), // nodeId to navigate to
  items: z.array(z.object({
    label: z.string(),
    target: z.string(),
  })).optional(), // sub-items for two-level menu
});

const TwoLevelMenuNodeSchema = BaseNodeSchema.extend({
  type: z.literal('TwoLevelMenu'),
  title: z.string().optional(),
  items: z.array(MenuItemSchema),
  defaultNext: z.string().optional(), // fallback if no selection
});

// ========== New Logic Element Node Schemas ==========
const RpaRecorderNodeSchema = BaseNodeSchema.extend({
  type: z.literal('RpaRecorder'),
  description: z.string(),
  recordingData: z.string().optional(), // JSON string of recorded steps
  outputVarId: z.string(), // GUID of the variable to store result
  next: z.string().optional(),
});

const AllocatorNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Allocator'),
  resourceType: z.string(), // e.g., 'agent', 'queue', 'department'
  allocationStrategy: z.enum(['round-robin', 'least-busy', 'skill-based', 'random']),
  targetVarId: z.string(), // GUID of the variable to store allocated resource
  next: z.string().optional(),
});

const DecisionPointNodeSchema = BaseNodeSchema.extend({
  type: z.literal('DecisionPoint'),
  title: z.string(),
  description: z.string().optional(),
  criteria: z.array(z.object({
    condition: ConditionSchema,
    label: z.string(),
    next: z.string(),
  })),
  defaultNext: z.string().optional(),
});

const EmailActionSchema = z.object({
  type: z.literal('Email'),
  to: z.string(),
  subject: z.string(),
  body: z.string(),
  dedupeKeyTemplate: z.string(),
});

const WebhookActionSchema = z.object({
  type: z.literal('Webhook'),
  url: z.string(),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH']),
  headers: z.record(z.string()).optional(),
  bodyTemplate: z.string().optional(),
  dedupeKeyTemplate: z.string(),
});

const SmsActionSchema = z.object({
  type: z.literal('Sms'),
  to: z.string(),
  body: z.string(),
  dedupeKeyTemplate: z.string(),
});

const ActionConfigSchema = z.discriminatedUnion('type', [
  EmailActionSchema,
  WebhookActionSchema,
  SmsActionSchema,
]);

const ActionNodeSchema = BaseNodeSchema.extend({
  type: z.literal('Action'),
  action: ActionConfigSchema,
  next: z.string().optional(),
});

// ========== Discriminated Union of All Nodes ==========
export const NodeSchema = z.discriminatedUnion('type', [
  StartNodeSchema,
  EndNodeSchema,
  NoteNodeSchema,
  PromptNodeSchema,
  FormNodeSchema,
  IfNodeSchema,
  BranchNodeSchema,
  SwitchNodeSchema,
  SetVarNodeSchema,
  ActionNodeSchema,
  // UI Elements
  PageNodeSchema,
  ContactUsNodeSchema,
  TwoLevelMenuNodeSchema,
  // Logic Elements
  RpaRecorderNodeSchema,
  AllocatorNodeSchema,
  DecisionPointNodeSchema,
]);

// ========== Variable Definitions ==========
export const VariableScopeSchema = z.enum(['global', 'flow', 'page']);

const VariableDefSchema = z.object({
  id: z.string(), // GUID — the durable key referenced everywhere via {{var:<id>}}
  name: z.string(), // display label only
  type: z.enum(['string', 'number', 'boolean', 'object']),
  scope: VariableScopeSchema.default('flow'),
  pageNodeId: z.string().optional(), // owning page node id, for scope 'page'
  defaultValue: z.any().optional(),
});

/**
 * Normalize the `variables` field to the flat, GUID-keyed array shape.
 * Accepts the legacy `{ global: [], session: [] }` object form (no ids) so
 * un-migrated DSLs can still be parsed/linted; legacy entries fall back to
 * id = name until the data migration rewrites them.
 */
function normalizeVariables(input: unknown): unknown {
  if (Array.isArray(input)) return input;
  if (input && typeof input === 'object') {
    const obj = input as { global?: any[]; session?: any[] };
    if ('global' in obj || 'session' in obj) {
      const out: any[] = [];
      (obj.global || []).forEach((v) => out.push({ scope: 'global', id: v.id ?? v.name, ...v }));
      (obj.session || []).forEach((v) => out.push({ scope: 'flow', id: v.id ?? v.name, ...v }));
      return out;
    }
  }
  return input ?? [];
}

// ========== Playbook DSL Schema ==========
export const PlaybookDSLSchema = z.object({
  schemaVersion: z.string().default('1.0'),
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  tenantId: z.string().optional(),
  variables: z.preprocess(normalizeVariables, z.array(VariableDefSchema)).default([]),
  nodes: z.array(NodeSchema),
});

// Backwards compatibility aliases
export const FlowDSLSchema = PlaybookDSLSchema;

// ========== Type Exports ==========
export type PlaybookDSL = z.infer<typeof PlaybookDSLSchema>;
export type PlaybookNode = z.infer<typeof NodeSchema>;

// Backwards compatibility aliases
export type FlowDSL = PlaybookDSL;
export type FlowNode = PlaybookNode;
export type StartNode = z.infer<typeof StartNodeSchema>;
export type EndNode = z.infer<typeof EndNodeSchema>;
export type NoteNode = z.infer<typeof NoteNodeSchema>;
export type PromptNode = z.infer<typeof PromptNodeSchema>;
export type FormNode = z.infer<typeof FormNodeSchema>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type IfNode = z.infer<typeof IfNodeSchema>;
export type BranchNode = z.infer<typeof BranchNodeSchema>;
export type BranchPath = z.infer<typeof BranchPathSchema>;
export type SwitchNode = z.infer<typeof SwitchNodeSchema>;
export type SwitchCase = z.infer<typeof SwitchCaseSchema>;
export type SetVarNode = z.infer<typeof SetVarNodeSchema>;
export type ActionNode = z.infer<typeof ActionNodeSchema>;
export type ActionConfig = z.infer<typeof ActionConfigSchema>;
export type EmailAction = z.infer<typeof EmailActionSchema>;
export type WebhookAction = z.infer<typeof WebhookActionSchema>;
export type SmsAction = z.infer<typeof SmsActionSchema>;
export type Condition = z.infer<typeof ConditionSchema>;
export type VariableDef = z.infer<typeof VariableDefSchema>;
export type VariableScope = z.infer<typeof VariableScopeSchema>;
// New node types
export type PageNode = z.infer<typeof PageNodeSchema>;
export type ContactUsNode = z.infer<typeof ContactUsNodeSchema>;
export type TwoLevelMenuNode = z.infer<typeof TwoLevelMenuNodeSchema>;
export type MenuItem = z.infer<typeof MenuItemSchema>;
export type RpaRecorderNode = z.infer<typeof RpaRecorderNodeSchema>;
export type AllocatorNode = z.infer<typeof AllocatorNodeSchema>;
export type DecisionPointNode = z.infer<typeof DecisionPointNodeSchema>;
